import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Form, Input } from 'antd';
import { postClients } from '../../store/MSIdentity/thunks';
import { UserRequest } from '../../store/MSIdentity/types';
import Logo from '../../globals/images/Layout/logo3.svg';
import styles from './Register.module.scss';

const passwordRules = [
  { required: true, message: 'La contraseña es obligatoria.' },
  { min: 8, message: 'Mínimo 8 caracteres.' },
  { pattern: /[A-Z]/, message: 'Debe tener al menos una mayúscula.' },
  { pattern: /[0-9]/, message: 'Debe tener al menos un número.' },
  { pattern: /[!@#$%^&*(),.?":{}|<>]/, message: 'Debe tener al menos un carácter especial (!@#$...).' },
];

const Register: React.FC = () => {
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const [form] = Form.useForm();

  const messageResponse = useSelector((state: any) => state.system?.messageResponse);
  const isLoading = useSelector((state: any) => state.system?.loadingIndicator);

  const handleSubmit = async (values: any) => {
    const payload: UserRequest = {
      firstName:      values.firstName,
      secondName:     values.secondName     || null,
      lastName:       values.lastName,
      secondLastName: values.secondLastName || null,
      email:          values.email,
      password:       values.password,
      dni:            values.dni            || null,
      cellPhone:      values.cellPhone      || null,
    };
    const result = await dispatch(postClients(payload) as any);
    if (result) {
      history.push('/login');
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>

        <div className={styles.logoArea}>
          <div className={styles.logoIcon}>
            <img src={Logo} alt="Glow Skin" />
          </div>
          <h1 className={styles.title}>Crear cuenta</h1>
        </div>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className={styles.form}
          requiredMark={false}
        >
          <div className={styles.row}>
            <Form.Item
              className={styles.field}
              name="firstName"
              label={<span className={styles.label}>Primer nombre</span>}
              rules={[{ required: true, message: 'El nombre es obligatorio.' }]}
            >
              <Input className={styles.input} placeholder="María" />
            </Form.Item>

            <Form.Item
              className={styles.field}
              name="secondName"
              label={
                <span className={styles.label}>
                  Segundo nombre <span className={styles.optional}>(opcional)</span>
                </span>
              }
            >
              <Input className={styles.input} placeholder="José" />
            </Form.Item>
          </div>

          <div className={styles.row}>
            <Form.Item
              className={styles.field}
              name="lastName"
              label={<span className={styles.label}>Apellido</span>}
              rules={[{ required: true, message: 'El apellido es obligatorio.' }]}
            >
              <Input className={styles.input} placeholder="García" />
            </Form.Item>

            <Form.Item
              className={styles.field}
              name="secondLastName"
              label={
                <span className={styles.label}>
                  Segundo apellido <span className={styles.optional}>(opcional)</span>
                </span>
              }
            >
              <Input className={styles.input} placeholder="López" />
            </Form.Item>
          </div>

          <Form.Item
            name="email"
            label={<span className={styles.label}>Correo electrónico</span>}
            rules={[
              { required: true, message: 'El correo es obligatorio.' },
              { type: 'email', message: 'Ingresa un correo válido.' },
            ]}
          >
            <Input className={styles.input} placeholder="correo@ejemplo.com" />
          </Form.Item>

          <div className={styles.row}>
            <Form.Item
              className={styles.field}
              name="dni"
              label={
                <span className={styles.label}>
                  Cédula <span className={styles.optional}>(opcional)</span>
                </span>
              }
              rules={[
                { pattern: /^\d{10}$/, message: 'Debe tener exactamente 10 dígitos.' },
              ]}
            >
              <Input
                className={styles.input}
                placeholder="1234567890"
                maxLength={10}
                onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
              />
            </Form.Item>

            <Form.Item
              className={styles.field}
              name="cellPhone"
              label={
                <span className={styles.label}>
                  Teléfono <span className={styles.optional}>(opcional)</span>
                </span>
              }
              rules={[
                { pattern: /^\d{10}$/, message: 'Debe tener exactamente 10 dígitos.' },
              ]}
            >
              <Input
                className={styles.input}
                placeholder="0991234567"
                maxLength={10}
                onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
              />
            </Form.Item>
          </div>

          <Form.Item
            name="password"
            label={<span className={styles.label}>Contraseña</span>}
            rules={passwordRules}
            hasFeedback
          >
            <Input.Password className={styles.input} placeholder="••••••••" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label={<span className={styles.label}>Confirmar contraseña</span>}
            dependencies={['password']}
            hasFeedback
            rules={[
              { required: true, message: 'Confirma tu contraseña.' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Las contraseñas no coinciden.'));
                },
              }),
            ]}
          >
            <Input.Password className={styles.input} placeholder="••••••••" />
          </Form.Item>

          {/* Botón solo habilitado cuando los campos requeridos están completos y sin errores */}
          <Form.Item shouldUpdate>
            {() => {
              const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
              const requiredTouched = form.isFieldsTouched(
                ['firstName', 'lastName', 'email', 'password', 'confirmPassword'],
                true
              );
              return (
                <button
                  className={styles.btn}
                  type="submit"
                  disabled={isLoading || hasErrors || !requiredTouched}
                >
                  {isLoading ? <span className={styles.spinner} /> : 'Registrarse'}
                </button>
              );
            }}
          </Form.Item>

        </Form>

        <button
          className={styles.loginBtn}
          type="button"
          onClick={() => history.push('/login')}
        >
          ¿Ya tienes cuenta? Inicia sesión
        </button>

        <p className={styles.footer}>© 2026 Glow Skin · Todos los derechos reservados</p>
      </div>
    </div>
  );
};

export default Register;
